import { useState } from 'react'
import { Link, useSearchParams, useLocation } from 'react-router-dom'
import { gql } from 'graphql-request'
import { truncate } from 'lodash'

import { useQuery } from '@hooks/graphql'
import { formatTimestamp } from '@helpers/format'
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell
} from '@/components/ui/table'
import Pagination from '@components/Pagination'
import SearchInput from '@components/SearchInput'
import { Loader2 } from 'lucide-react'

import NoResults from './NoResults'
import { MessageSquare, CheckCircle2, XCircle } from 'lucide-react'

const TUTORS_QUERY = gql`
  query adminTutors($page: Int!, $search: String) {
    adminTutors(page: $page, perPage: 10, search: $search) {
      nodes {
        id
        name
        createdAt
        summary
        approvedForCommunity
        image {
          url
        }
        userName
      }
      pagesCount
      nodesCount
    }
  }
`

const TutorList = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()
  const page = Number(searchParams.get('page')) || 1
  const search = searchParams.get('search') || ''
  const [searchTerm, setSearchTerm] = useState(search)

  const { data: { adminTutors: { nodes: tutors = [], pagesCount } = {} } = {}, isLoading } = useQuery({
    queryKey: ['adminTutors', page, search],
    gqlQuery: TUTORS_QUERY,
    variables: { page, search }
  })

  const updateParams = newParams => {
    const params = {
      page,
      search,
      ...newParams
    }

    if (params.page === 1) delete params.page
    if (params.search === '') delete params.search

    setSearchParams(params)
  }

  const clearFilters = () => {
    setSearchTerm('')
    setSearchParams({})
  }

  const clearSearch = () => {
    setSearchTerm('')
    updateParams({ search: '', page: 1 })
  }

  return (
    <>
      <div className='m-5'>
        <SearchInput
          className='mb-5'
          value={searchTerm}
          onChange={event => setSearchTerm(event.target.value)}
          onSearch={() => updateParams({ search: searchTerm, page: 1 })}
          onClear={clearSearch}
        />

        <Choose>
          <When condition={isLoading}>
            <div className="flex justify-center mt-64">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          </When>

          <When condition={tutors.length < 1 && !isLoading}>
            <NoResults clearFilters={clearFilters} />
          </When>

          <Otherwise>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[5%]"></TableHead>
                  <TableHead className="w-[20%]">Name</TableHead>
                  <TableHead className="w-[10%]">Educator</TableHead>
                  <TableHead className="w-[35%]">Summary</TableHead>
                  <TableHead className="w-[20%] text-center">Approved</TableHead>
                  <TableHead className="w-[10%]">Created at</TableHead>
                </TableRow>
              </TableHeader>

              <TableBody>
                <For each='tutor' of={tutors}>
                  <TableRow key={tutor.id}>
                    <TableCell>
                      <Link
                        className='block'
                        to={`${tutor.id}`}
                        state={{ previousListParams: location.search }}
                      >
                        <Choose>
                          <When condition={tutor.image?.url}>
                            <img src={tutor.image?.url} alt={tutor.name} className='w-10 h-10 rounded-md object-cover' />
                          </When>

                          <Otherwise>
                            <div className='flex justify-center items-center w-10 h-10 rounded-md bg-muted'>
                              <MessageSquare className='w-6 h-6 text-muted-foreground m-auto' />
                            </div>
                          </Otherwise>
                        </Choose>
                      </Link>
                    </TableCell>

                    <TableCell>
                      <Link
                        className='block'
                        to={`${tutor.id}`}
                        state={{ previousListParams: location.search }}
                      >
                        {tutor.name}
                      </Link>
                    </TableCell>

                    <TableCell>
                      <Link
                        className='block'
                        to={`${tutor.id}`}
                        state={{ previousListParams: location.search }}
                      >
                        {tutor.userName}
                      </Link>
                    </TableCell>

                    <TableCell>
                      <Link
                        className='flex'
                        to={`${tutor.id}`}
                        state={{ previousListParams: location.search }}
                      >
                        {truncate(tutor.summary, { length: 70 })}
                      </Link>
                    </TableCell>

                    <TableCell className="text-center">
                      <Link
                        className='inline-flex justify-center'
                        to={`${tutor.id}`}
                        state={{ previousListParams: location.search }}
                      >
                        <Choose>
                          <When condition={tutor.approvedForCommunity}>
                            <CheckCircle2 className='w-6 h-6' />
                          </When>

                          <Otherwise>
                            <XCircle className='w-6 h-6' />
                          </Otherwise>
                        </Choose>
                      </Link>
                    </TableCell>

                    <TableCell>
                      <Link
                        className='block'
                        to={`${tutor.id}`}
                        state={{ previousListParams: location.search }}
                      >
                        {formatTimestamp(tutor.createdAt)}
                      </Link>
                    </TableCell>
                  </TableRow>
                </For>
              </TableBody>
            </Table>

            <Pagination page={page} pagesCount={pagesCount} setPage={newPage => updateParams({ page: newPage })} />
          </Otherwise>
        </Choose>
      </div>
    </>
  )
}

export default TutorList
